/**
 * **A ship's two 90 cm projectors**, and the rate limiter that everything on
 * her deck swings on.
 *
 * Split out of `scene-ships.ts` when that file went past aislop's size ceiling
 * (2026-09-01, the damage lane), and the boundary is a real one rather than a
 * filing convenience: `scene-ships.ts` is the *pool* — which hulls are drawn,
 * where they are, what their mountings are pointing at — and this is the one
 * thing on a ship whose rule is somebody else's. Her beams are
 * `flight/searchlights.ts`'s own law reached through its exported pieces, not
 * reimplemented, and the only reason a ship needs its own entry point at all is
 * that the ground collector walks searchlight **posts** and a ship has none:
 * her lights are nodes on her own rig.
 *
 * ## Nothing here decides anything
 *
 * `beamSeed` off her battery's id so two ships never sweep in step, `sweepAim`
 * for the idle scissor, and her own `FlakBattery`'s slot targets for the
 * tracking half — every one of those is the searchlight module's. What this file
 * adds is the same thing `scene-ships.ts` adds to a gun mounting: **rate
 * limiting**, so a beam swings toward its bearing rather than snapping to it.
 * That is presentation. It changes no hashed state and no peer has to agree
 * about it.
 *
 * ## Her lights go out with her guns
 *
 * The caller is expected to gate on `shipFightsOn` and does. This module has no
 * opinion about it, on purpose: "she has stopped" is a sim predicate with
 * exactly one definition (`ship-damage.ts`), and a renderer that re-derived it
 * from a burning flag or an integrity threshold would be a second definition
 * waiting to drift.
 */

import * as THREE from "three/webgpu";
import {
  SHIP_LIGHT_OFFSETS_M,
  shipBatteryId,
  type EncounterState,
  type FlakBattery,
  type ShipSpec,
} from "@red-opus/engine";
import type { SearchlightBeams } from "../three/searchlight-beams.ts";
import type { ShipModel } from "../three/ship-mounts.ts";
import { beamSeed, seededUnit, sweepAim, type AimVector } from "./searchlights.ts";

/** Beams a ship throws: her two 90 cm projectors (ship-design.md §3.3). */
export const LIGHTS_PER_SHIP = SHIP_LIGHT_OFFSETS_M.length;

/** One mounting's current pose, carried between frames so it can be slewed rather than snapped. */
export interface MountPose {
  azimuthRad: number;
  elevationRad: number;
}

/** Turns `slew` degrees a second into one frame's step and moves `pose` that far toward the target. */
export function slewTo(
  pose: MountPose,
  azimuthRad: number,
  elevationRad: number,
  slewDegPerS: number,
  dt: number,
): void {
  const limit = ((slewDegPerS * Math.PI) / 180) * dt;
  pose.azimuthRad += clampStep(wrapAngle(azimuthRad - pose.azimuthRad), limit);
  pose.elevationRad += clampStep(elevationRad - pose.elevationRad, limit);
}

export function wrapAngle(radians: number): number {
  const turn = 2 * Math.PI;
  return ((((radians + Math.PI) % turn) + turn) % turn) - Math.PI;
}

function clampStep(delta: number, limit: number): number {
  return Math.max(-limit, Math.min(limit, delta));
}

const scratchAim: AimVector = { x: 0, y: 1, z: 0 };
const scratchLens = new THREE.Vector3();
const scratchDirection = new THREE.Vector3();

export interface ShipLightsInput {
  readonly model: ShipModel;
  /** Carried between frames by the caller's `ShipView`, so a beam slews rather than snaps. */
  readonly poses: MountPose[];
  readonly ship: ShipSpec;
  readonly state: EncounterState;
  readonly beams: SearchlightBeams;
  readonly headingRad: number;
  readonly dt: number;
}

/**
 * Her two projectors: sweeping on their own seeded scissor, or on whatever her
 * battery is laid on.
 *
 * A ship whose battery has left the flak list — pass 0 drops it the moment she
 * stops fighting — simply has nothing laid, which is the same answer a battery
 * with empty slots gives. The caller does not call this for such a ship anyway;
 * that it would degrade gracefully is a property and not a plan.
 */
export function pushShipLights(input: ShipLightsInput): void {
  const { model, ship, state, beams, headingRad, dt } = input;
  const battery = state.flak.find((entry: FlakBattery) => entry.id === shipBatteryId(ship.id));
  const laid = battery?.slots.map((slot) => slot.targetId).filter((id): id is string => id !== null) ?? [];
  for (const [index, pose] of input.poses.entries()) {
    const seed = beamSeed(shipBatteryId(ship.id), index);
    const targetId = laid.length === 0 ? null : laid[index % laid.length];
    const target = targetId === null ? undefined : state.aircraft.find((entry) => entry.id === targetId);
    // The lens has to be found before the beam can be aimed at anything, and it
    // moves with the mount, so it is read at last frame's pose and used to
    // decide this one's — a frame of lag on a beam apex at 45 m/s is 0.75 m.
    model.lensPosition(index, scratchLens);
    if (target === undefined) {
      sweepAim(seed, state.timeS, scratchAim);
    } else {
      scratchDirection
        .set(
          target.sim.position.x - scratchLens.x,
          target.sim.position.y - scratchLens.y,
          target.sim.position.z - scratchLens.z,
        )
        .normalize();
      scratchAim.x = scratchDirection.x;
      scratchAim.y = scratchDirection.y;
      scratchAim.z = scratchDirection.z;
    }
    // The yoke is a child of the hull, so its angle is the aim in the **ship's
    // own frame**: the world bearing less her head. Her elevation is the
    // world's, because at a cruising helm she carries no pitch worth the
    // correction — and once she is going down her lights are already out.
    const wantedAzimuth = wrapAngle(Math.atan2(scratchAim.x, -scratchAim.z) - headingRad);
    const wantedElevation = Math.asin(Math.max(-1, Math.min(1, scratchAim.y)));
    slewTo(pose, wantedAzimuth, wantedElevation, 40, dt);
    model.aimLight(index, pose.azimuthRad, pose.elevationRad);
    model.lensPosition(index, scratchLens);
    if (!model.lensDirection(index, scratchDirection)) continue;
    beams.push(
      scratchLens.x,
      scratchLens.y,
      scratchLens.z,
      scratchDirection.x,
      scratchDirection.y,
      scratchDirection.z,
      // A ship's lights are the same 90 cm projector a battery's are, at the
      // same three intensities; the seeded jitter keeps a pair from reading as
      // one lamp with two barrels.
      target === undefined ? 0.55 + 0.05 * seededUnit(seed, 5) : 1,
    );
  }
}
